"use client";

import { ReactNode, useEffect, useRef, useState } from "react";

import Image from "next/image";
import React from "react";
import clsx from "clsx";

interface CarouselProps {
  items: ReactNode[];
  itemWidth?:
    | string
    | { default: string; md?: string; lg?: string; xl?: string };
  showControls?: boolean;
  showPageIndicator?: boolean;
  invertArrowColor?: boolean;
  itemClassName?: string;
  hideScrollbar?: boolean;
}

export default function Carousel({
  items,
  itemWidth = "90%",
  showControls = true,
  showPageIndicator = true,
  invertArrowColor = false,
  itemClassName,
  hideScrollbar = true,
}: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [responsiveWidth, setResponsiveWidth] = useState<string>(
    typeof itemWidth === "string" ? itemWidth : itemWidth.default
  );

  // Handle responsive itemWidth
  useEffect(() => {
    if (typeof itemWidth === "object") {
      const handleResize = () => {
        const width = window.innerWidth;
        if (width >= 1280 && itemWidth.xl) {
          setResponsiveWidth(itemWidth.xl);
        } else if (width >= 1024 && itemWidth.lg) {
          setResponsiveWidth(itemWidth.lg);
        } else if (width >= 768 && itemWidth.md) {
          setResponsiveWidth(itemWidth.md);
        } else {
          setResponsiveWidth(itemWidth.default);
        }
      };

      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [itemWidth]);

  // Create modified items with current index prop
  const itemsWithCurrentIndex = items.map((item) => {
    if (React.isValidElement(item)) {
      // Pass currentIndex as a prop to each item
      return React.cloneElement(item, {
        carouselIndex: currentIndex,
      } as React.HTMLAttributes<unknown>);
    }
    return item;
  });

  // Update currentIndex on scroll
  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    const handleScroll = () => {
      const children = Array.from(container.children);
      const scrollLeft = container.scrollLeft;
      let minDiff = Infinity;
      let activeIdx = 0;
      children.forEach((child, idx) => {
        const el = child as HTMLElement;
        // Find the left edge of the card relative to the container
        const diff = Math.abs(el.offsetLeft - scrollLeft);
        if (diff < minDiff) {
          minDiff = diff;
          activeIdx = idx;
        }
      });
      setCurrentIndex(activeIdx);
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const isAtStart = currentIndex === 0;
  const isAtEnd = currentIndex === items.length - 1;

  const scrollToPrev = () => {
    const container = carouselRef.current;
    if (container) {
      container.scrollBy({
        left: -container.offsetWidth * 0.8,
        behavior: "smooth",
      });
    }
  };

  const scrollToNext = () => {
    const container = carouselRef.current;
    if (container) {
      container.scrollBy({
        left: container.offsetWidth * 0.9,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div
        ref={carouselRef}
        className={clsx(
          "w-full overflow-x-auto flex scroll-snap-x-mandatory",
          hideScrollbar && "no-scrollbar"
        )}
        style={{ scrollSnapType: "x mandatory" }}
      >
        {itemsWithCurrentIndex.map((item, i) => (
          <div
            key={i}
            className={clsx(
              "border-t border-b border-l border-neutral-200 p-8 flex flex-col items-start shadow-sm last:mr-0",
              itemClassName
            )}
            style={{
              minWidth: responsiveWidth,
              maxWidth: responsiveWidth,
              scrollSnapAlign: "start",
            }}
          >
            {item}
          </div>
        ))}
      </div>
      {showControls && (
        <div className="w-full flex items-center justify-between mt-4 px-2">
          {/* Left Arrow */}
          <button
            className="p-2 disabled:opacity-50"
            onClick={scrollToPrev}
            disabled={isAtStart}
            aria-label="Previous"
          >
            <Image
              src="/icons/arrowRight.svg"
              alt="Previous"
              width={48}
              height={48}
              className={`w-10 h-10 rotate-180 ${
                isAtStart ? "brightness-0 saturate-100 invert-[0.85]" : ""
              } ${invertArrowColor ? "invert-[0.85]" : ""}`}
            />
          </button>
          {/* Page number for carousel */}
          {showPageIndicator && (
            <span className="font-medium text-sm">
              {currentIndex + 1}/{items.length}
            </span>
          )}
          {/* Right Arrow */}
          <button
            className="p-2 disabled:opacity-50"
            onClick={scrollToNext}
            disabled={isAtEnd}
            aria-label="Next"
          >
            <Image
              src="/icons/arrowRight.svg"
              alt="Next"
              width={48}
              height={48}
              className={`w-10 h-10 ${
                isAtEnd ? "brightness-0 saturate-100 invert-[0.85]" : ""
              } ${invertArrowColor ? "invert-[0.85]" : ""}`}
            />
          </button>
        </div>
      )}
    </div>
  );
}
